import type { WorkspaceFile } from "../../sandbox/client/api";

/**
 * The previews a Files panel file can have. Anything else (HTML, SVG, scripts, unknown types)
 * has none and only downloads: nothing from the workspace is rendered on our origin as markup.
 */
export type PreviewKind = "image" | "pdf" | "csv" | "markdown" | "json";

const KINDS_BY_EXTENSION: Record<string, PreviewKind> = {
  png: "image",
  jpg: "image",
  jpeg: "image",
  gif: "image",
  webp: "image",
  pdf: "pdf",
  csv: "csv",
  md: "markdown",
  markdown: "markdown",
  json: "json",
};

/** No preview is fetched for a file larger than this; it says so and offers the download. */
export const PREVIEW_MAX_BYTES: Record<PreviewKind, number> = {
  image: 15 * 1024 * 1024,
  pdf: 25 * 1024 * 1024,
  csv: 5 * 1024 * 1024,
  markdown: 1024 * 1024,
  json: 1024 * 1024,
};

/** A PDF shows its first pages only. */
export const PDF_PREVIEW_PAGES = 3;
/** A CSV shows a table of at most this many rows and columns. */
export const CSV_PREVIEW_ROWS = 200;
export const CSV_PREVIEW_COLUMNS = 30;
/** A CSV cell shows at most this many characters, then an ellipsis. */
export const CSV_PREVIEW_CELL_CHARS = 1_000;
/** JSON shows at most this many characters, pretty-printed. */
export const JSON_PREVIEW_CHARS = 100_000;
/** A PDF page is drawn on a canvas of at most this many pixels (a 4000 by 4000 square). */
export const MAX_CANVAS_PIXELS = 16_000_000;
/** Nor more than this many pixels along either side (browsers refuse longer canvases). */
export const MAX_CANVAS_SIDE = 16_384;
/**
 * An image whose header says it is larger than this many pixels is not shown (the browser would
 * decode all of it, 4 bytes a pixel): it says so and offers the download.
 */
export const MAX_IMAGE_PIXELS = 50_000_000;

/**
 * The preview for `file`, from its name's extension and its folder (the API's class), never from
 * a type the file could choose. Null when it has none.
 */
export function previewKind(file: Pick<WorkspaceFile, "path" | "class">): PreviewKind | null {
  if (file.class !== "publish" && file.class !== "uploads") return null;
  const name = file.path.split("/").pop() ?? "";
  const dot = name.lastIndexOf(".");
  if (dot <= 0) return null;
  return KINDS_BY_EXTENSION[name.slice(dot + 1).toLowerCase()] ?? null;
}
